import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../shared/api';
import { startOfWeekMonIso, todayLocalIso, fmtDate, dayOfWeekIso, addDaysIso } from '../../shared/dates';
import { ScheduleGrid, type Block } from './ScheduleGrid';
import { ServiceLabels } from '@census/shared';
import type { ClientItineraryResponse } from '@census/shared';

export function PrintClientWeekPage() {
  const { id } = useParams<{ id: string }>();
  const { data: client } = useQuery({
    queryKey: ['client', id],
    queryFn: () => api.get(`/clients/${id}`).then((r) => r.data),
    enabled: !!id,
  });

  // Use the week of the first upcoming instance so the print always shows a
  // full schedule rather than a partial week when intake was just committed.
  const { data: week } = useQuery({
    queryKey: ['client-week', id],
    queryFn: async () => {
      // Try this week first; if it has no individual instances, try next week.
      const thisWeek = startOfWeekMonIso(todayLocalIso());
      const nextWeek = addDaysIso(thisWeek, 7);
      const [thisData, nextData] = await Promise.all([
        api.get(`/schedule/client/${id}`, { params: { weekStart: thisWeek } }).then((r) => r.data),
        api.get(`/schedule/client/${id}`, { params: { weekStart: nextWeek } }).then((r) => r.data),
      ]);
      const hasIndividual = (data: any) =>
        (data.instances ?? []).some((i: any) => i.kind === 'INDIVIDUAL');
      // Prefer this week if it has individual appointments, otherwise next week.
      if (hasIndividual(thisData)) return { ...thisData, weekStart: thisWeek };
      return { ...nextData, weekStart: nextWeek };
    },
    enabled: !!id,
  });

  const weekStart: string = week?.weekStart ?? startOfWeekMonIso(todayLocalIso());
  const { data: settings } = useQuery({
    queryKey: ['clinic-info'],
    queryFn: () => api.get('/settings/clinic_info').then((r) => r.data),
  });

  // AI itinerary mutation — fires once when client+week are ready. Posts the
  // current week's sessions and gets back warm plain-English descriptions for
  // the printable handoff sheet.
  const itinerary = useMutation({
    mutationFn: (payload: any) =>
      api
        .post<ClientItineraryResponse>('/scheduling/client-itinerary', payload)
        .then((r) => r.data),
  });

  const itineraryPayload = useMemo(() => {
    if (!client || !week) return null;
    const sessions = (week.instances ?? []).map((i: any) => ({
      day: dayOfWeekIso(i.scheduledDate),
      startMin: i.startMin,
      endMin: i.endMin,
      serviceType: i.serviceType,
      resourceName: i.resourceName ?? null,
      resourceType: i.resourceType ?? i.kind ?? null,
      roomName: i.roomName ?? null,
    }));
    return {
      client: {
        firstName: client.firstName,
        lastName: client.lastName,
        cohort: client.cohort,
        cohortName: client.cohortName ?? null,
        iopTrackName: client.iopTrackName ?? null,
        nursingGroupDay: client.nursingGroupDay ?? null,
        requiredServices: client.requiredServices ?? [],
      },
      weekStart,
      weekEnd: addDaysIso(weekStart, 6),
      sessions,
    };
  }, [client, week, weekStart]);

  // Fire the itinerary call once when the payload becomes available.
  useEffect(() => {
    if (itineraryPayload && itinerary.status === 'idle') {
      itinerary.mutate(itineraryPayload);
    }
  }, [itineraryPayload]);

  // Auto-print only after BOTH the week + the itinerary attempt have settled.
  // If the AI errors, still print (the page renders without the itinerary section).
  useEffect(() => {
    if (!week) return;
    if (itinerary.status === 'pending' || itinerary.status === 'idle') return;
    const t = setTimeout(() => window.print(), 350);
    return () => clearTimeout(t);
  }, [week, itinerary.status]);

  if (!client || !week) return <div className="p-6 text-sm">Loading…</div>;

  // While the AI itinerary is being generated, show a full-page loader instead
  // of the half-rendered handoff sheet. The auto-print is gated on this too,
  // so the print dialog only opens once the page is fully ready.
  if (itinerary.status === 'idle' || itinerary.status === 'pending') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3 text-slate-700">
          <span
            className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-slate-700"
            aria-hidden="true"
          />
          <p className="text-sm font-medium">Generating itinerary…</p>
          <p className="text-2xs text-slate-500">This usually takes a few seconds.</p>
        </div>
      </div>
    );
  }

  // Sub-line on each block: room for group services (so client knows which
  // physical room to walk to), provider name for individual visits. Falls back
  // to the resource displayName. Adds a 📹 hint when delivered via telehealth.
  const blocks: Block[] = (week.instances ?? []).map((i: any) => {
    const isGroup = i.resourceType === 'GROUP' || i.kind === 'GROUP';
    let sub = i.resourceName ?? '';
    if (isGroup && i.roomName) sub = `${i.resourceName} · 📍 ${i.roomName}`;
    if (!isGroup && i.resourceName) sub = `with ${i.resourceName}`;
    if (i.viaTelehealth) sub = `${sub} · 📹 Telehealth`;
    return {
      day: dayOfWeekIso(i.scheduledDate),
      startMin: i.startMin,
      endMin: i.endMin,
      label: ServiceLabels[i.serviceType as 'THERAPY'] ?? i.serviceType,
      sub,
      kind: i.kind,
      status: i.status,
    };
  });

  return (
    <div className="min-h-screen bg-white p-6">
      <header className="mb-4 flex items-start justify-between border-b border-slate-300 pb-3">
        <div>
          <div className="text-base font-semibold text-slate-900">
            {settings?.clinicName ?? 'Clinic'}
          </div>
          <div className="text-xs text-slate-500">{settings?.clinicAddress}</div>
          <div className="text-xs text-slate-500">{settings?.clinicPhone}</div>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold">Weekly Program</div>
          <div className="text-xs text-slate-500">
            Week of {fmtDate(weekStart)} → {fmtDate(addDaysIso(weekStart, 6))}
          </div>
        </div>
      </header>

      <section className="mb-3">
        <h1 className="text-xl font-semibold text-slate-900">
          {client.cohortId} — {client.firstName} {client.lastName}
        </h1>
        <p className="text-sm text-slate-600">
          Cohort {client.cohort} · IOP: {client.iopTrackName ?? '—'} · Nursing day: {client.nursingGroupDay ?? '—'}
        </p>
      </section>

      {/* AI-generated weekly itinerary — printed BEFORE the calendar grid so
          the handoff sheet leads with plain-English descriptions. The
          pending/loading state is handled by the full-page loader above. */}
      {itinerary.status === 'success' && itinerary.data && (
        // Outer section flows freely; only individual day blocks get
        // break-inside-avoid so a single day doesn't split across pages.
        // Putting break-inside-avoid on the outer wrapper pushed the whole
        // section to a new page and left the previous page mostly blank.
        <section className="mb-5 border-b border-slate-300 pb-4">
          <h2 className="mb-2 text-base font-semibold text-slate-900">Weekly Service Schedule Summary</h2>
          {itinerary.data.intro && (
            <p className="mb-3 text-sm leading-relaxed text-slate-700">{itinerary.data.intro}</p>
          )}
          {itinerary.data.days.map((d) => (
            <div key={d.day} className="mb-3 break-inside-avoid">
              <h3 className="text-sm font-semibold text-slate-900">{d.day}</h3>
              <p className="text-sm text-slate-700">You will attend:</p>
              <div className="mt-1 space-y-2 pl-3">
                {d.sessions.map((s, i) => (
                  <div key={i} className="text-sm">
                    <p className="font-medium text-slate-900">
                      {s.time}: {s.service}
                    </p>
                    {s.description && (
                      <p className="text-slate-600">{s.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          {itinerary.data.summary && (
            <div className="mt-3 border-t border-slate-200 pt-2">
              <h3 className="text-sm font-semibold text-slate-900">Simple Explanation</h3>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                {itinerary.data.summary}
              </p>
            </div>
          )}
        </section>
      )}

      <ScheduleGrid blocks={blocks} />

      <section className="mt-4 text-xs text-slate-600">
        <h2 className="mb-1 text-sm font-semibold text-slate-700">Service legend</h2>
        <ul className="list-disc pl-5">
          {(client.requiredServices ?? []).map((s: string) => (
            <li key={s}>{ServiceLabels[s as 'THERAPY']}</li>
          ))}
        </ul>
      </section>

      <section className="mt-6 grid grid-cols-2 gap-4 border-t border-slate-300 pt-4 text-xs text-slate-600">
        <div>
          <h3 className="text-sm font-semibold">Attendance policy</h3>
          <p>Two consecutive no-shows trigger case review. Call {settings?.clinicPhone} to reschedule.</p>
        </div>
        <div>
          <h3 className="text-sm font-semibold">Crisis &amp; after-hours</h3>
          <p>Crisis line: {settings?.crisisLine ?? '988'} (24/7)</p>
        </div>
      </section>

      <section className="mt-8 grid grid-cols-2 gap-12 text-xs">
        <div>
          Client signature: <span className="ml-2 inline-block w-48 border-b border-slate-400" /> Date: <span className="ml-2 inline-block w-24 border-b border-slate-400" />
        </div>
        <div>
          Staff signature: <span className="ml-2 inline-block w-48 border-b border-slate-400" /> Date: <span className="ml-2 inline-block w-24 border-b border-slate-400" />
        </div>
      </section>
    </div>
  );
}
